/**
 * Lane C0-PROPS: shared parts of the plains building kit (render/kit/*), on top of the painters in buildings.ts
 * (materials, `wall` / `top` / `box` / `roof`, which note their faces for route C rotation, `faceRec`).
 *
 * Wuxia detail at scene density: key-pattern (回纹) bands, painted bracket bands, gold fittings, red tassels at the
 * eave corners, roofs by function (red = homes and trade, green = temples and gardens, grey = official), light from
 * the upper right. Decals painted inside a face (bands, plaques, doors) are carried by that face's texture when the
 * camera turns; free-standing bits (tassels, ridge beasts, finials) stand upright at their point.
 */
import { hash2, valueNoise } from '@nine/shared';
import { eaveLift, eaveLine, eavePt, hipLine } from './curvedRoof';
import {
  box,
  faceRec,
  fill,
  line,
  M,
  Pix,
  roof,
  snap,
  wall,
  type Proj,
  type Pt,
  type Ramp,
  type RGB,
  type Side,
  type Tex,
} from '../buildings';
import type { Sprite } from '../scenery';

export type RoofColour = 'red' | 'grey' | 'green' | 'wood';

const ramp = (...hex: string[]): Ramp =>
  hex.map((h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)] as const);

/** Extra ramps the kit needs beside buildings.ts `M`. */
export const K = {
  /** Red glazed roof tiles (homes and trade): warmer and brighter than `M.terra`. */
  redTile: ramp('#1e0808', '#3a0d0e', '#5a1414', '#7e2019', '#8e2a20', '#a83a2c'),
  /** Grey official tiles: a blue-grey, lighter than `M.slate` so the big roofs read at a distance. */
  greyTile: ramp('#0d0f14', '#161a22', '#202630', '#2c3441', '#3e4a5c', '#586878'),
  /** Green glazed tiles (temples, gardens, pavilions). */
  greenTile: ramp('#0a1a18', '#12292a', '#1b3e3a', '#27534c', '#3a6f62', '#5a9684'),
  /** Thatch and bark (camps, sheds). */
  thatch: ramp('#3a2a16', '#5a4224', '#7a5c32', '#9a7842', '#b89658', '#d2b274'),
  /** Maroon lacquer (master palette): darker than `M.lacquer`, for trims and the hall's columns. */
  maroon: ramp('#2a0c10', '#45131a', '#621c22', '#82282c', '#a23a36'),
  /** Mud and wet earth (the buffalo wallow, stockade feet). */
  mud: ramp('#2a2016', '#3e3020', '#56442c', '#6e5838', '#8a7048'),
  /** Iron (bars, hoops, braziers, weapons). */
  iron: ramp('#16181c', '#262a30', '#3a4048', '#56606a', '#7a8692', '#a8b4be'),
  /** Straw (dummies, hay, mats). */
  straw: ramp('#4a3818', '#6e5626', '#927436', '#b4944a', '#d4b464', '#ecd286'),
  /** Cloth: indigo, ochre and white of awnings and banners. */
  indigo: ramp('#141a30', '#1e2a4a', '#2c3e68', '#3e5686', '#5672a2'),
  ochre: ramp('#4a3010', '#74501a', '#9c7026', '#c29238', '#e0b452'),
  /** Kit plaster: brighter lights and deeper shade than `M.plaster`, for stronger material contrast. */
  wallPlaster: ramp('#3c3334', '#5e5148', '#867860', '#a89a7c', '#c2b490', '#d8c8a4'),
  cream: ramp('#6e6450', '#968a6e', '#bcb08e', '#dcd2ae', '#f2ead0'),
} as const;

export const ROOF_TILES: Record<RoofColour, Ramp> = {
  red: K.redTile,
  grey: K.greyTile,
  green: K.greenTile,
  wood: K.thatch,
};

/** A canvas for a footprint `w` × `h` (tiles) and `Z` art pixels tall, with `pad` pixels round; anchor = north corner. */
export function stage(w: number, h: number, Z: number, pad = 28): { px: Pix; P: Proj; ax: number; ay: number } {
  const W = Math.ceil((w + h) * 16 + pad * 2);
  const H = Math.ceil(Z + (w + h) * 8 + pad * 2);
  const px = new Pix(W, H);
  const ax = h * 16 + pad;
  const ay = Z + pad;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  return { px, P, ax, ay };
}
/**
 * The kit's finish: a darker 1-px outline than buildings.ts `finish` (round 3: the sheets' crisp ink edges), the
 * bordered colour kept only faintly, a touch lighter on the lit (right, top) side.
 */
export function kitFinish(px: Pix, ax: number, ay: number): Sprite {
  const { w, h, d } = px;
  const o = new Uint8ClampedArray(d);
  const ink = [10, 7, 12] as const;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (d[i + 3]) continue;
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      const add = (j: number, keep: number) => {
        if (!d[j + 3]) return;
        r += ink[0] + (d[j]! - ink[0]) * keep;
        g += ink[1] + (d[j + 1]! - ink[1]) * keep;
        b += ink[2] + (d[j + 2]! - ink[2]) * keep;
        n++;
      };
      if (x > 0) add(i - 4, 0.16);
      if (y < h - 1) add(i + w * 4, 0.16);
      if (x < w - 1) add(i + 4, 0.08);
      if (y > 0) add(i - w * 4, 0.06);
      if (!n) continue;
      o[i] = r / n;
      o[i + 1] = g / n;
      o[i + 2] = b / n;
      o[i + 3] = 255;
    }
  d.set(o);
  const c = px.toCanvas();
  return { img: c, ax, ay };
}
export const done = (s: { px: Pix; ax: number; ay: number }): Sprite => kitFinish(s.px, s.ax, s.ay);

/**
 * The shadow an eave throws on what is already painted under it: for each screen column, the pixels from the eave's
 * lower edge (the near edges of the eave rectangle at `ze`) down `d` px are darkened, deepest at the eave. Call it
 * after the walls and before the roof.
 */
export function eaveShadow(px: Pix, P: Proj, [x0, y0, x1, y1]: readonly number[], ze: number, d = 12, lift = 0): void {
  const edges: [Pt, Pt][] = [];
  const seg = (ax: number, ay: number, bx: number, by: number): void => {
    const n = lift > 0 ? 12 : 1;
    for (let i = 0; i < n; i++) edges.push([eavePt(P, ax, ay, bx, by, ze, lift, i / n), eavePt(P, ax, ay, bx, by, ze, lift, (i + 1) / n)]);
  };
  seg(x0!, y1!, x1!, y1!);
  seg(x1!, y1!, x1!, y0!);
  const at = (x: number): number | null => {
    let best: number | null = null;
    for (const [a, b] of edges) {
      const lo = Math.min(a[0], b[0]);
      const hi = Math.max(a[0], b[0]);
      if (x < lo || x > hi || hi === lo) continue;
      const y = a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0]);
      if (best === null || y > best) best = y;
    }
    return best;
  };
  const xs = edges.flat().map((p) => p[0]);
  for (let x = Math.floor(Math.min(...xs)); x <= Math.ceil(Math.max(...xs)); x++) {
    const ye = at(x + 0.5);
    if (ye === null) continue;
    for (let k = 0; k < d; k++) {
      const y = Math.round(ye) + k;
      if (x < 0 || y < 0 || x >= px.w || y >= px.h) continue;
      const i = (y * px.w + x) * 4;
      if (!px.d[i + 3]) continue;
      const m = k < 2 ? 0.24 : k < d * 0.6 ? 0.38 : 0.38 + ((k - d * 0.6) / (d * 0.4)) * 0.45;
      px.d[i] = px.d[i]! * m;
      px.d[i + 1] = px.d[i + 1]! * m;
      px.d[i + 2] = Math.min(255, px.d[i + 2]! * m * 1.12);
    }
  }
}

/**
 * Round 3: the roof's lit edges, 1 px, for crisp silhouettes (the sheets' look): the near eave lines just above the
 * fascia, the hips from the three near corners, the ridge top. Light from the upper right: the right-hand edges
 * brightest. Call after `roof`.
 */
export function roofEdges(
  px: Pix,
  P: Proj,
  [x0, y0, x1, y1]: readonly number[],
  ze: number,
  [tx0, ty0, tx1, ty1]: readonly number[],
  zt: number,
  tiles: Ramp,
): void {
  const hi = tiles[tiles.length - 1]!;
  const mid = tiles[tiles.length - 2]!;
  const up = (p: Pt, k: number): Pt => [p[0], p[1] - k];
  const k = eaveLift([x0!, y0!, x1!, y1!], zt - ze);
  if (k > 0) {
    eaveLine(px, P, [x0!, y1!], [x1!, y1!], ze + 0.5, k, () => mid);
    eaveLine(px, P, [x1!, y1!], [x1!, y0!], ze + 0.5, k, () => hi);
    hipLine(px, P, [x1!, y1!], [tx1!, ty1!], ze, zt, k, hi);
    hipLine(px, P, [x1!, y0!], [tx1!, ty0!], ze, zt, k, hi);
    hipLine(px, P, [x0!, y1!], [tx0!, ty1!], ze, zt, k, mid);
  } else {
    line(px, up(P(x0!, y1!, ze), 2), up(P(x1!, y1!, ze), 2), () => mid);
    line(px, up(P(x1!, y1!, ze), 2), up(P(x1!, y0!, ze), 2), () => hi);
    line(px, P(x1!, y1!, ze), P(tx1!, ty1!, zt), () => hi);
    line(px, P(x1!, y0!, ze), P(tx1!, ty0!, zt), () => hi);
    line(px, P(x0!, y1!, ze), P(tx0!, ty1!, zt), () => mid);
  }
  if (tx0 !== tx1 || ty0 !== ty1) line(px, up(P(tx0!, ty0!, zt), 2), up(P(tx1!, ty1!, zt), 2), () => hi);
}

/** 回纹: one cell of the key pattern, 6 × 6 (1: ink). */
const KEY = ['111110', '100010', '101010', '101110', '100000', '111111'];
const keyAt = (u: number, v: number): boolean => KEY[((v % 6) + 6) % 6]![((u % 6) + 6) % 6] === '1';

/** A band of key pattern in gold on a field ramp, 6 px high from z0 (texture for `wall`). */
export const keyBand =
  (z0: number, field: Ramp = K.maroon, ink: Ramp = M.gold): Tex =>
  (u, v, l) =>
    keyAt(Math.floor(u), Math.floor(v - z0)) ? snap(ink, l + 0.2, u | 0, v | 0, 0) : snap(field, l, u | 0, v | 0, 0.2);

/** Painted bracket band (斗拱 seen as a frieze): blocks in jade, blue and gold with dark joints, under eaves. */
export const brackets =
  (z0: number, z1: number): Tex =>
  (u, v, l) => {
    const k = Math.floor(u / 6);
    const r = v - z0;
    const H = z1 - z0;
    if (u % 6 < 1) return M.dark[1]!;
    if (r > H - 2) return snap(M.gold, l + 0.15, u | 0, v | 0, 0);
    if (r < 2) return snap(K.maroon, l, u | 0, v | 0, 0);
    // A cap block on every other bracket, a stepped arm on the others.
    const cap = k % 2 === 0 ? r > H * 0.45 : r > H * 0.7;
    if (!cap) return snap(K.maroon, l - 0.15, u | 0, v | 0, 0);
    return snap(k % 4 === 0 ? M.jade : k % 4 === 2 ? M.blue : M.gold, l + (r > H - 4 ? 0.1 : -0.05), u | 0, v | 0, 0);
  };

/** A painted beam with a medallion every bay (彩画): blue and jade with a gold rim. */
export const paintedBeam =
  (z0: number, z1: number, bay = 16): Tex =>
  (u, v, l) => {
    const r = v - z0;
    const H = z1 - z0;
    if (r < 1 || r > H - 1) return snap(M.gold, l + 0.1, u | 0, v | 0, 0);
    const m = Math.abs(((u % bay) + bay) % bay - bay / 2);
    if (m < H * 0.45 && Math.abs(r - H / 2) < H * 0.32) return snap(m < 2 ? M.gold : M.red, l + 0.05, u | 0, v | 0, 0);
    return snap(Math.floor(u / bay) % 2 ? M.jade : M.blue, l, u | 0, v | 0, 0.2);
  };

/** A square column on a drum base, in `mat`, with a gold capital band. */
export function column(px: Pix, P: Proj, x: number, y: number, r: number, z0: number, z1: number, mat: Ramp = M.lacquer): void {
  box(px, P, [x - r * 1.5, y - r * 1.5, x + r * 1.5, y + r * 1.5], z0, z0 + 5, stoneTex, stoneTex);
  box(px, P, [x - r, y - r, x + r, y + r], z0 + 5, z1, (u, v, l) => {
    const edge = Math.abs(u - (x + r) * 16) < 1.2 || Math.abs(u - (y - r) * 16) < 1.2;
    if (v > z1 - 4) return snap(M.gold, l + 0.1, u | 0, v | 0, 0);
    return snap(mat, l + (edge ? 0.18 : 0) + (valueNoise(u / 3, v / 12) - 0.5) * 0.08, u | 0, v | 0);
  });
}
const stoneTex: Tex = (u, v, l) => snap(M.stone, l + (hash2(u | 0, v | 0) - 0.5) * 0.12, u | 0, v | 0, 0.2);

/** A rail (balustrade) along a front face from u0 to u1 at `at`: a top rail, balusters, a foot rail. */
export function rail(px: Pix, P: Proj, side: Side, at: number, u0: number, u1: number, z0: number, h: number, mat: Ramp = M.lacquer, gap = 5): void {
  wall(px, P, side, at, u0, u1, z0 + h - 3, z0 + h, (u, v, l) => snap(mat, l + 0.12, u | 0, v | 0, 0));
  wall(px, P, side, at, u0, u1, z0, z0 + 2, (u, v, l) => snap(mat, l - 0.1, u | 0, v | 0, 0));
  wall(px, P, side, at, u0, u1, z0 + 2, z0 + h - 3, (u, v, l) =>
    Math.round(u) % gap < 2 ? snap(mat, l - 0.05, u | 0, v | 0, 0) : null,
  );
}

/** Stone steps rising toward -x (on the front-right face, x = at) or -y (front-left, y = at), `n` treads. */
export function steps(px: Pix, P: Proj, side: Side, at: number, u0: number, u1: number, rise: number, n: number, depth = 0.22): void {
  for (let k = 0; k < n; k++) {
    const z1 = (rise * (n - k)) / n;
    const d = at + depth * (n - k - 1) + depth;
    if (side === 'right') box(px, P, [at, u0, d, u1], 0, z1, stoneTex, flagsLite);
    else box(px, P, [u0, at, u1, d], 0, z1, stoneTex, flagsLite);
  }
}
const flagsLite: Tex = (u, v, l) =>
  snap(M.stone, l + 0.12 + ((Math.floor(u / 12) + Math.floor(v / 6)) % 2 ? 0.04 : -0.04), u | 0, v | 0, 0.2);

/** A red tassel hanging from `p`: a cord, a gold knot and a red skirt. */
export function tassel(px: Pix, p: Pt, len = 9): void {
  const [x, y] = [Math.round(p[0]), Math.round(p[1])];
  px.rect(x, y, 1, 3, M.dark[2]!);
  px.rect(x - 1, y + 3, 3, 2, M.gold[3]!);
  for (let k = 0; k < len - 5; k++) {
    px.set(x - 1, y + 5 + k, M.red[1]!);
    px.set(x, y + 5 + k, M.red[3]!);
    px.set(x + 1, y + 5 + k, k < 2 ? M.red[2]! : M.red[1]!);
  }
}

/** Tassels under the three near eave corners of an eave rectangle. */
export function cornerTassels(px: Pix, P: Proj, [x0, y0, x1, y1]: readonly number[], ze: number): void {
  for (const [cx, cy] of [
    [x0!, y1!],
    [x1!, y1!],
    [x1!, y0!],
  ] as const) {
    const [ex, ey] = P(cx, cy, ze);
    tassel(px, [ex, ey + 1], 8);
  }
}

/** Ridge beasts (鴟吻 seen small): a curled gold-trimmed end ornament at each ridge end. */
export function ridgeEnds(px: Pix, P: Proj, a: readonly [number, number, number], b: readonly [number, number, number], tiles: Ramp, big = 1): void {
  for (const [q, dir] of [
    [a, -1],
    [b, 1],
  ] as const) {
    const [rx, ry] = P(q[0], q[1], q[2]);
    const x = Math.round(rx);
    const y = Math.round(ry);
    const h = 6 + big * 3;
    for (let k = 0; k < h; k++) {
      const w = k < h - 3 ? 3 : 2;
      for (let i = 0; i < w; i++) px.set(x - 1 + i + (k > h - 4 ? dir : 0), y - k, tiles[i === 0 ? 1 : i === 1 ? 3 : 2]!);
    }
    // The curl, tipped outward, and a gold fleck.
    px.set(x + dir * 2, y - h + 1, tiles[3]!);
    px.set(x + dir * 2, y - h + 2, tiles[2]!);
    px.set(x, y - h + 2, M.gold[3]!);
  }
}

/** A gold finial (pearl on rings) standing at `p`. */
export function finial(px: Pix, p: Pt, h = 10): void {
  const [x, y] = [Math.round(p[0]), Math.round(p[1])];
  for (let k = 0; k < h; k++) px.rect(x - (k % 3 === 0 ? 1 : 0), y - k, k % 3 === 0 ? 3 : 2, 1, snap(M.gold, 0.6 + (k % 3) * 0.12, k, 0, 0));
  for (let b = -2; b <= 2; b++)
    for (let a = -2; a <= 2; a++) if (a * a + b * b <= 5) px.set(x + a, y - h - 2 + b, snap(M.gold, 0.95 - (a + b) * 0.1, a, b, 0));
}

/**
 * A hip-and-gable roof (歇山): a hip skirt from the eave rectangle up to a smaller rectangle at `zg`, then a gable
 * roof along x from there to the ridge at `zt`, its front-right end a gable triangle with a gold ornament. Notes its
 * faces (the hip and gable slopes through `roof`, the gable as a wall).
 */
export function hipGable(
  px: Pix,
  P: Proj,
  eaves: readonly [number, number, number, number],
  ze: number,
  inset: number,
  zg: number,
  zt: number,
  tiles: Ramp,
  edge: Ramp = M.gold,
): void {
  const [x0, y0, x1, y1] = eaves;
  const cy = (y0 + y1) / 2;
  const gx0 = x0 + inset;
  const gx1 = x1 - inset;
  const e = (y1 - y0) / 2 - inset;
  eaveShadow(px, P, eaves, ze, 20, eaveLift(eaves, zg - ze));
  roof(px, P, eaves, ze, [gx0, cy - e, gx1, cy + e], zg, tiles, edge, false);
  roofEdges(px, P, eaves, ze, [gx0, cy - e, gx1, cy + e], zg, tiles);
  roof(px, P, [gx0, cy - e, gx1, cy + e], zg, [gx0, cy, gx1, cy], zt, tiles, edge);
  roofEdges(px, P, [gx0, cy - e, gx1, cy + e], zg, [gx0, cy, gx1, cy], zt, tiles);
  // The gable: plaster in a timber frame, a gold hanging fish (懸魚) at its apex.
  const tri: Pt[] = [P(gx1, cy - e, zg), P(gx1, cy + e, zg), P(gx1, cy, zt - 2)];
  if (faceRec.on)
    faceRec.faces.push({ kind: 'wall', pts: [[gx1, cy - e, zg], [gx1, cy + e, zg], [gx1, cy, zt - 2], [gx1, cy, zt - 2]] });
  const [axp, ayp] = P(gx1, cy, zt - 2);
  fill(px, tri, (x, y) => {
    const d = y - ayp;
    if (d < 3) return snap(tiles, 0.7, x, y, 0);
    const frame = Math.abs(x - axp) % 9 < 1;
    if (frame) return snap(M.timber, 0.6, x, y, 0);
    return snap(M.plaster, 0.62 - d * 0.004 + (valueNoise(x / 4, y / 6) - 0.5) * 0.1, x, y);
  });
  const fishY = Math.round(ayp) + 5;
  for (let k = 0; k < 9; k++) {
    const w = k < 6 ? 1 : k < 8 ? 2 : 1;
    for (let a = -w; a <= w; a++) px.set(Math.round(axp) + a, fishY + k, snap(M.gold, 0.8 - Math.abs(a) * 0.2, a, k, 0));
  }
  // Barge boards along the gable's two rakes.
  line(px, P(gx1, cy - e, zg), P(gx1, cy, zt - 2), () => edge[edge.length - 2]!, 1);
  line(px, P(gx1, cy + e, zg), P(gx1, cy, zt - 2), () => edge[2]!, 1);
}

/**
 * A gable roof (two slopes, ridge along x), its front-right end a plain gable wall in `gable` (plaster, boards or
 * brick), barge boards on the rake. For warehouses, stables, long houses.
 */
export function gableRoof(
  px: Pix,
  P: Proj,
  eaves: readonly [number, number, number, number],
  ze: number,
  zt: number,
  tiles: Ramp,
  gable: Tex,
  edge: Ramp = M.stone,
  wallAt?: number,
): void {
  const [x0, y0, x1, y1] = eaves;
  const cy = (y0 + y1) / 2;
  // The gable stands at the eave end, in place of the roof's own end triangle (`wallAt` is kept for callers).
  void wallAt;
  const gx = x1 - 0.01;
  eaveShadow(px, P, eaves, ze, 16, eaveLift(eaves, zt - ze));
  roof(px, P, eaves, ze, [x0, cy, x1, cy], zt, tiles, edge);
  roofEdges(px, P, eaves, ze, [x0, cy, x1, cy], zt, tiles);
  const tri: Pt[] = [P(gx, y0 + 0.3, ze), P(gx, y1 - 0.3, ze), P(gx, cy, zt - 3)];
  if (faceRec.on)
    faceRec.faces.push({ kind: 'wall', pts: [[gx, y0 + 0.3, ze], [gx, y1 - 0.3, ze], [gx, cy, zt - 3], [gx, cy, zt - 3]] });
  const [, ayp] = P(gx, cy, zt - 3);
  fill(px, tri, (x, y) => gable(x, ze + (P(gx, cy, ze)[1] - y), 0.66 - (y - ayp) * 0.002));
  line(px, P(x1, y0, ze), P(x1, cy, zt), () => edge[edge.length - 2]!, 2);
  line(px, P(x1, y1, ze), P(x1, cy, zt), () => edge[1]!, 2);
}

/** A cloth awning sloping out from a wall face: stripes along it, a scalloped valance. */
export function awning(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z: number,
  out: number,
  drop: number,
  cloth: Ramp,
  stripe: Ramp = K.cream,
  poles = false,
): void {
  const Q = (u: number, o: number, zz: number): Pt => (side === 'left' ? P(u, at + o, zz) : P(at + o, u, zz));
  const a = Q(u0, out, z - drop);
  const b = Q(u1, out, z - drop);
  const c = Q(u1, 0, z);
  const d = Q(u0, 0, z);
  const pts: Pt[] = [a, b, c, d];
  if (faceRec.on) {
    const R = (u: number, o: number, zz: number) => (side === 'left' ? ([u, at + o, zz] as const) : ([at + o, u, zz] as const));
    faceRec.faces.push({ kind: 'roof', pts: [R(u0, out, z - drop), R(u1, out, z - drop), R(u1, 0, z), R(u0, 0, z)] });
  }
  const lit = side === 'right' ? 0.72 : 0.5;
  fill(px, pts, (x, y) => {
    const st = Math.floor((side === 'right' ? x * 0.5 - y : x * 0.5 + y) / 4) % 2 === 0;
    return snap(st ? cloth : stripe, lit + (valueNoise(x / 5, y / 3) - 0.5) * 0.1, x, y, 0.2);
  });
  // Valance: scallops along the outer edge.
  const n = Math.max(2, Math.round((u1 - u0) * 3));
  for (let k = 0; k <= n * 4; k++) {
    const f = k / (n * 4);
    const [vx, vy] = Q(u0 + (u1 - u0) * f, out, z - drop);
    const sc = 2 + Math.round(Math.abs(Math.sin(f * n * Math.PI)) * 2);
    for (let q = 0; q < sc; q++) px.set(vx, vy + q, snap(cloth, lit - 0.15 - q * 0.05, Math.round(vx), q, 0));
  }
  // Poles at the corners (the back ones stand only when the awning is free of a wall: `poles`).
  for (const [u, o, zz] of [
    [u0, out, z - drop],
    [u1, out, z - drop],
    ...(poles ? ([[u0, 0, z], [u1, 0, z]] as const) : []),
  ] as const) {
    const [bx, by] = Q(u, o, 0);
    const [, ty] = Q(u, o, zz);
    for (let y = Math.round(ty); y < by; y++) {
      px.set(bx, y, M.wood[3]!);
      px.set(bx + 1, y, M.wood[1]!);
    }
  }
}

/** A hanging vertical signboard (招牌) on a front face: lacquer, gold border and glyph strokes. */
export function signboard(px: Pix, P: Proj, side: Side, at: number, u: number, z1: number, h: number, field: Ramp = M.dark): void {
  const w = 0.5;
  wall(px, P, side, at + 0.06, u - w / 2, u + w / 2, z1 - h, z1, (uu, v, l) => {
    const a = (u - w / 2) * 16;
    const b = (u + w / 2) * 16;
    if (uu - a < 1.2 || b - uu < 1.2 || v - (z1 - h) < 1.2 || z1 - v < 1.2) return snap(M.gold, l + 0.2, uu | 0, v | 0, 0);
    const k = Math.floor(((z1 - v) / h) * 3);
    const cy = Math.abs((((z1 - v) / h) * 3) % 1 - 0.5);
    const cx = Math.abs((uu - a) / (b - a) - 0.5);
    if (cx < 0.26 && cy < 0.3 && hash2(Math.round(uu) + k * 17, Math.round(v)) > 0.35) return snap(M.gold, 0.75, uu | 0, v | 0, 0);
    return snap(field, l + 0.25, uu | 0, v | 0, 0);
  });
}

/** A cloth banner hanging from a crossbar at `top` (screen point), `w` × `h` px, with a band, a mark and a fringe. */
export function clothBanner(px: Pix, top: Pt, w: number, h: number, cloth: Ramp, mark: Ramp = M.gold, sway = 1): void {
  const [x0, y0] = [Math.round(top[0]), Math.round(top[1])];
  px.rect(x0 - 2, y0 - 1, w + 4, 2, M.wood[2]!);
  px.set(x0 - 3, y0 - 1, M.gold[3]!);
  px.set(x0 + w + 2, y0 - 1, M.gold[3]!);
  for (let y = 0; y < h; y++) {
    const s = Math.round(Math.sin(y / 7) * sway);
    for (let x = 0; x < w; x++) {
      const l = 0.5 + (x / w) * 0.25 + (Math.sin((x + y * 0.4) / 2.4) > 0.7 ? -0.12 : 0);
      const band = y < 3 || (y >= h - 6 && y < h - 4);
      const border = x === 0 || x === w - 1;
      const m = Math.abs(x - (w - 1) / 2) < w * 0.22 && y > h * 0.25 && y < h * 0.7 && (y - Math.floor(h * 0.25)) % 5 < 3;
      px.set(x0 + x + s, y0 + 1 + y, band || border ? snap(M.gold, l, x, y, 0) : m ? snap(mark, l + 0.1, x, y, 0) : snap(cloth, l, x, y));
    }
    // A fringe of tails at the bottom.
    if (y === h - 1) for (let x = 0; x < w; x += 2) px.rect(x0 + x + s, y0 + 2 + y, 1, 3, cloth[1]!);
  }
}

/** A sprite of an upright prop: a little canvas `w` × `h` with the foot point at (`ax`, `ay`). */
export function propPix(w: number, h: number, ax: number, ay: number): { px: Pix; P: Proj; ax: number; ay: number } {
  const px = new Pix(w, h);
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  return { px, P, ax, ay };
}

/** An ellipse of pixels (jars, pots, round tops), lit from the upper right. */
export function blob(px: Pix, cx: number, cy: number, rx: number, ry: number, r: Ramp, base = 0.55): void {
  for (let b = -Math.ceil(ry); b <= Math.ceil(ry); b++)
    for (let a = -Math.ceil(rx); a <= Math.ceil(rx); a++) {
      if ((a * a) / (rx * rx + 0.01) + (b * b) / (ry * ry + 0.01) > 1) continue;
      px.set(cx + a, cy + b, snap(r, base + (a / (rx + 1)) * 0.25 - (b / (ry + 1)) * 0.2, cx + a, cy + b, 0.3));
    }
}

/** Light for a colour step (for hand-placed pixels). */
export const tone = (r: Ramp, l: number): RGB => r[Math.max(0, Math.min(r.length - 1, Math.round(l * (r.length - 1))))]!;
